import React, { useEffect, useRef, useState } from "react";
import PhotoSwipeLightbox from "photoswipe/lightbox";
import "photoswipe/style.css";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { set } from "react-hook-form";
import Loader from "../../../components/Loader";
import AiLoader from "../../../components/AiLoader";
import {
  getMinioSize,
  getUpdatedImageUrlWithSize,
} from "../../../utils/helpers";
import Img from "../../../components/Img";
import MasonaryWrapper from "../../../components/MasonaryWrapper";
import { Spinner } from "@chakra-ui/react";
import ImgOptimized from "../../../components/ImgOptimized";
import { createRoot } from "react-dom/client";
import { Icon } from "@iconify/react";

// Icon config for Render and Actual tags
const TAG_CONFIG = {
  render: {
    label: "Render",
    icon: <Icon icon="catppuccin:3d" className="w-3.5 h-3.5" />,
    color: "bg-[#008080]",
  },
  actual: {
    label: "Actual",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className="w-3.5 h-3.5"
      >
        <path d="M12 15.2A3.2 3.2 0 1 0 12 8.8a3.2 3.2 0 0 0 0 6.4z" />
        <path d="M9 2L7.17 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-3.17L15 2H9zm3 15a5 5 0 1 1 0-10 5 5 0 0 1 0 10z" />
      </svg>
    ),
    color: "bg-[#008080]",
  },
  all: {
    label: "All",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className="w-3.5 h-3.5"
      >
        <path d="M3 3h8v8H3V3zm10 0h8v8h-8V3zm0 10h8v8h-8v-8zM3 13h8v8H3v-8z" />
      </svg>
    ),
    color: "",
  },
};

const ProjectGallery = ({ sliderImages, imageTags = [] }) => {
  const galleryRef = useRef(null);
  const [imageDimensions, setImageDimensions] = useState([]);
  const [isLoadingImages, setIsLoadingImages] = useState(true);
  const [tagFilter, setTagFilter] = useState("all");

  const getTagForImage = (img) => {
    const match = imageTags.find((t) => t.image === img);
    return match ? match.tag : "actual";
  };

  const filteredImages = sliderImages.filter((img) => {
    if (tagFilter === "all") return true;
    return getTagForImage(img) === tagFilter;
  });

  const TAG_COLORS = {
    render: "#008080",
    actual: "#008080",
  };

  useEffect(() => {
    const lightbox = new PhotoSwipeLightbox({
      gallery: galleryRef.current,
      children: "a",
      pswpModule: () => import("photoswipe"),
      secondaryZoomLevel: 2,
      maxZoomLevel: 4,
    });

    lightbox.on("uiRegister", function () {
      lightbox.pswp.ui.registerElement({
        name: "tag-badge",
        order: 9,
        isButton: false,
        appendTo: "bar",
        html: "",
        onInit: (el, pswp) => {
          Object.assign(el.style, {
            display: "flex",
            alignItems: "center",
            gap: "5px",
            padding: "3px 10px",
            borderRadius: "999px",
            color: "#fff",
            fontSize: "11px",
            fontWeight: "600",
            margin: "auto 8px",
            pointerEvents: "none",
            flexShrink: "0",
          });

          const root = createRoot(el);

          const updateBadge = () => {
            const anchor = pswp.currSlide?.data?.element;
            const tag = anchor?.getAttribute("data-tag") || "actual";
            const label = tag.charAt(0).toUpperCase() + tag.slice(1);
            el.style.backgroundColor = TAG_COLORS[tag] || TAG_COLORS.actual;
            const cfg = TAG_CONFIG[tag] || TAG_CONFIG.actual;
            root.render(
              <span className="flex items-center gap-1.5">
                {cfg.icon}
                <span>{label}</span>
              </span>,
            );
          };

          pswp.on("change", updateBadge);
          updateBadge();

          pswp.on("destroy", () => {
            setTimeout(() => {
              root.unmount();
            }, 0);
          });
        },
      });
    });

    lightbox.init();

    return () => {
      lightbox.destroy();
    };
  }, []);

  useEffect(() => {
    const fetchImageDimensions = async () => {
      try {
        const dimensions = await Promise.all(
          sliderImages.map(async (item) => {
            try {
              const image = new Image();
              image.src = getUpdatedImageUrlWithSize(
                `${process.env.REACT_APP_BACK_URL}/api/minio/${item}`,
                [2, 2, 3],
              );
              await image.decode();
              return {
                src: item,
                width: image.naturalWidth || image.width || 1920,
                height: image.naturalHeight || image.height || 1080,
              };
            } catch (error) {
              console.error("Error fetching image dimensions:", error);
              return { src: item, width: 1920, height: 1080 };
            }
          }),
        );
        setImageDimensions(dimensions);
      } catch (error) {
        console.error("Error fetching image dimensions:", error);
      }
    };

    fetchImageDimensions();
    const timer = setTimeout(() => {
      setIsLoadingImages(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [sliderImages]);

  const getCountForTag = (tagOption) => {
    if (tagOption === "all") return sliderImages.length;
    return sliderImages.filter((img) => getTagForImage(img) === tagOption)
      .length;
  };

  return (
    <>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mb-6 my-2">
        <span className="text-sm font-semibold text-gray-700">
          Filter Images:
        </span>
        <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50 shadow-sm">
          {["all", "actual", "render"].map((tagOption) => {
            const count = getCountForTag(tagOption);
            const cfg = TAG_CONFIG[tagOption];
            return (
              <button
                key={tagOption}
                type="button"
                onClick={() => setTagFilter(tagOption)}
                className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-colors capitalize flex items-center gap-1.5 ${
                  tagFilter === tagOption
                    ? "bg-primary-500 text-white shadow-sm"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                }`}
              >
                {cfg.icon}
                {cfg.label} ({count})
              </button>
            );
          })}
        </div>
      </div>
      {isLoadingImages && (
        <div className=" min-h-[80svh] w-full h-fit absolute top-0  z-[5]">
          <MasonaryWrapper>
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                style={{ height: `${Math.random() * 100 + 150}px` }}
                className="bg-gray-200 animate-pulse w-full rounded-xl"
              />
            ))}
          </MasonaryWrapper>
        </div>
      )}
      <div
        ref={galleryRef}
        className={`${isLoadingImages ? "opacity-0" : "opacity-100"} relative mb-5`}
      >
        <MasonaryWrapper>
          {filteredImages.map((item, idx) => {
            const originalIndex = sliderImages.indexOf(item);
            const tag = getTagForImage(item);
            const cfg = TAG_CONFIG[tag] || TAG_CONFIG.actual;
            const dims = imageDimensions?.[originalIndex];
            return (
              <a
                key={idx}
                target="_blank"
                href={getUpdatedImageUrlWithSize(
                  `${process.env.REACT_APP_BACK_URL}/api/minio/${item}`,
                  [2, 2, 3],
                )}
                data-pswp-width={dims?.width || 1920}
                data-pswp-height={dims?.height || 1080}
                data-tag={tag}
                className="relative rounded-md sm:rounded-xl cursor-pointer block group"
              >
                <ImgOptimized
                  className="rounded-md sm:rounded-xl w-full h-full object-cover"
                  src={getUpdatedImageUrlWithSize(
                    `${process.env.REACT_APP_BACK_URL}/api/minio/${item}`,
                    [1, 1, 2],
                  )}
                  alt="Main project"
                />
                {/* Tag badge — icon only, tooltip on hover */}
                <span
                  className={`absolute bottom-2 left-2 ${cfg.color} text-white p-1.5 rounded-full flex items-center justify-center shadow-md`}
                  title={cfg.label}
                >
                  {cfg.icon}
                </span>
              </a>
            );
          })}
        </MasonaryWrapper>
      </div>
    </>
  );
};

export default ProjectGallery;
